import { FormControlLabel, Switch, Typography } from '@mui/material';
import React from 'react';
import { useNewDesign } from '../../state/DesignPreferenceContextProvider';
import { useTranslation } from 'react-i18next';
import { spaces } from '../../assets/styles/themes/tokens';
import { OnboardingHint } from '../OnboardingHint';
import { onboardingHints, onboardingHintsMaxTimes } from '../../constants';
import moment from 'moment';

interface NewDesignSwitchProps {
  onChange?: (newPreference: boolean) => void;
}

export const NewDesignSwitch: React.FC<NewDesignSwitchProps> = (props) => {
  const { t } = useTranslation();
  const { prefersNewDesign, setNewDesignPreference } = useNewDesign();
  const localStorageLargeOnboardingKey: string = `onboarding-${onboardingHints.newUIBanner}`;
  const localStorageLargeOnboardingValue: string = localStorage.getItem(localStorageLargeOnboardingKey) || 'true';
  const isLargeOnboarded: boolean = localStorageLargeOnboardingValue === 'true';

  const localStorageNewUISmallBannerKey: string = `hint-${onboardingHints.newUISmallBannerStage3}`;

  let localStorageNewUISmallBannerValueObject: { date?: string, times?: number } = {};
  const localStorageNewUISmallBannerValueStringObject = localStorage.getItem(localStorageNewUISmallBannerKey);
  if (localStorageNewUISmallBannerValueStringObject !== null) {
    try {
      localStorageNewUISmallBannerValueObject = JSON.parse(localStorageNewUISmallBannerValueStringObject);
    }
    catch (error) {  // Parsing will fail if the value is not a valid JSON string eg it used to be a date string
      localStorageNewUISmallBannerValueObject = {};
    }
  }

  const localStorageNewUISmallBannerValueDate: string =
  localStorageNewUISmallBannerValueObject?.date || moment().format();
  const localStorageNewUISmallBannerValueTimes: number =
  localStorageNewUISmallBannerValueObject?.times || 0;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props?.onChange) {
      props.onChange(event.target.checked);
    }
    setNewDesignPreference(event.target.checked);
    localStorage.setItem(localStorageNewUISmallBannerKey, JSON.stringify({
      date: moment().add(1, 'weeks').format(),
      // this is practically setting the value to the max value, so the hint will not be shown again
      times: onboardingHintsMaxTimes,
    }));    
  };

  const isWithHint: boolean = isLargeOnboarded && 
    moment(localStorageNewUISmallBannerValueDate).isSameOrBefore(moment()) &&
    localStorageNewUISmallBannerValueTimes < onboardingHintsMaxTimes.newUISmallBannerStage3;

  const SwitchWithHint = <OnboardingHint
    isNew={false}
    id={onboardingHints.newUISmallBannerStage3}
    description={ t('Did you know? With just one click you can use myLessons in the new design.') }
    placement='bottom-end'
    maxWidth={291}
    disablePortal={false}
    mobilePlacement='bottom-end'
    mobileMaxWidth={291}
    onClose={() => {
      localStorage.setItem(localStorageNewUISmallBannerKey, JSON.stringify({
        date: moment().add(1, 'weeks').format(),
        times: localStorageNewUISmallBannerValueTimes + 1,
      }));
    }}
  >
    <Switch
      checked={prefersNewDesign}
      onChange={(event) => onChange(event)}
    />
  </OnboardingHint>;

  const DesignSwitch = (
    <FormControlLabel
      labelPlacement='start'
      sx={{
        display: 'flex',
        gap: spaces.xs,
        margin: 0,
      }}
      control={
        !prefersNewDesign && isWithHint ? SwitchWithHint : <Switch
          checked={prefersNewDesign}
          onChange={(event) => onChange(event)}
        />
      }
      label={
        <Typography variant='subtitle2' color='text.secondary'>
          {t('New Design')}
        </Typography>
      }
    />
  );

  return DesignSwitch;
};

export default NewDesignSwitch;
